import { FRAMES, PIXEL_TYPE } from "odyssey-shared";
import { KIT_METRICS } from "../../ui/kit/metrics.ts";
import { pixelTextWidth } from "../../ui/kit/pixel-text.ts";
import { say } from "../../words.ts";
import type { Question } from "../ask.ts";
import { actionName } from "../controls/actions.ts";
import type { RefusedReason } from "../controls/capture.ts";
import { MASTER, type LevelId } from "../audio/audio-settings.ts";
import type { GameMenuItem } from "./state.ts";
import type { SettingsSection } from "./settings-model.ts";

/**
 * The game menu's and Settings' wording that takes a decision: which words an item, a count, a refused key or a
 * section's reset reads as, and how wide a dialog must be for its title. The words themselves are the language
 * files' (`menu.*`, `settings.*` and `controls.*` in `odyssey-lang`, ADR-119); a panel that needs one label as it
 * stands asks `say` for it.
 */

/**
 * The width of a dialog that holds its centred title on one line in the panel-title face: the title, the
 * dialog's padding on both sides and its frame. The design draws Change character's question and Settings'
 * "Apply your changes?" 200 wide with the title on two lines; the kit's dialog title is one line, as Log out
 * everywhere's is.
 *
 * @param title The dialog's title.
 * @returns The width in logical pixels.
 */
export function oneLineDialogWidth(title: string): number {
  return PIXEL_TYPE["panel-title"] * pixelTextWidth(title) + FRAMES.dialog.insets.left + 2 * KIT_METRICS.dialog.padding + FRAMES.dialog.insets.right;
}

/**
 * The menu's accessible name, which is the menu button's.
 *
 * @returns The name.
 */
export function menuLabel(): string {
  return say("hud.gameMenu");
}

/**
 * A menu item's word. An item that opens another window reads that window's own word, so the two cannot differ:
 * Emotes is the action's name in Controls, Change look the look screen's, Feedback the form's and Log out
 * everywhere that dialog's confirm.
 *
 * @param item The item.
 * @returns Its word.
 */
export function menuItemName(item: GameMenuItem): string {
  switch (item) {
    case "emotes":
      return actionName("emotes");
    case "look":
      return say("look.change.item");
    case "feedback":
      return say("feedback.item");
    case "everywhere":
      return say("account.logoutEverywhere.confirm");
    default:
      return say(`menu.items.${item}`);
  }
}

/**
 * Change character's question, as the design words it, its title on one line.
 *
 * @returns The question.
 */
export function changeQuestion(): Question {
  const title = say("menu.changeQuestion.title");
  return { title, body: say("menu.changeQuestion.body"), confirm: say("menu.changeQuestion.confirm"), tone: "primary", width: oneLineDialogWidth(title) };
}

/**
 * A level's label.
 *
 * @param id Master or a channel.
 * @returns The label.
 */
export function levelLabel(id: LevelId): string {
  return id === MASTER ? say("settings.audio.master") : say(`settings.audio.channels.${id}`);
}

/**
 * The width of the dialog that asks whether to apply pending changes, its title on one line.
 *
 * @returns The width in logical pixels.
 */
export function unsavedWidth(): number {
  return oneLineDialogWidth(say("settings.unsavedTitle"));
}

/**
 * The footer's count of changed rows, as the design words it ("1 change", "2 changes").
 *
 * @param count How many rows are changed, at least 1.
 * @returns The count's words.
 */
export function changesNote(count: number): string {
  return say("settings.changes", { count });
}

/**
 * What the dialog that asks whether to apply pending changes says. The design's sentence names one section
 * ("You changed 2 settings in Display."); changes can lie in several, so this one names none.
 *
 * @param count How many rows are changed, at least 1.
 * @param cost What applying would cost the viewer (ADR-093 decision 10: a smaller view or HUD is always said
 *   beside Apply), or null when it costs nothing.
 * @returns The sentence, the cost after it.
 */
export function unsavedSentence(count: number, cost: string | null = null): string {
  const changed = say("settings.unsaved", { count });
  return cost === null ? changed : `${changed} ${cost}`;
}

/**
 * The note for a key that cannot be bound. Each is short, and none explains the rule behind it; a key the menu
 * holds gets no note.
 *
 * @param key The key's label.
 * @param reason Why it was refused.
 * @returns The line.
 */
export function refusedKeyNote(key: string, reason: RefusedReason): string {
  switch (reason) {
    case "menu":
      return "";
    case "unnamed":
      return say("controls.refused.unnamed");
    case "shortcut":
      return say("controls.refused.shortcut");
    default:
      return say("controls.refused.unusable", { key });
  }
}

/**
 * A key cap's accessible name.
 *
 * @param action The action's name.
 * @param key The key's label.
 * @param changed Whether the row is changed.
 * @returns The name.
 */
export function capName(action: string, key: string, changed: boolean = false): string {
  return changed ? say("controls.capChanged", { action, key }) : say("controls.cap", { action, key });
}

/**
 * Reset this tab's question for a section, as the design words it for Display.
 *
 * @param section The section shown.
 * @returns The confirm dialog's title and its sentence.
 */
export function resetQuestion(section: SettingsSection): { readonly title: string; readonly body: string } {
  const name = say(`settings.sections.${section}`);
  return { title: say("settings.resetQuestion.title", { section: name }), body: say("settings.resetQuestion.body", { section: name }) };
}
